import { Anchor, Group, Loader, Space, Text, Title } from '@mantine/core'
import { observer } from 'mobx-react-lite'

import { WarningAlert } from '#components/Alerts'
import { useUpgrade } from '#components/Modals'
import { Toast } from '#components/Toast'
import { IOrg, useMst } from '#state'
import { pluralize } from '#util'

import {
  formatPlanDate,
  membersToRemove,
  ScheduledPlan,
  scheduledPlanName,
} from '../Org/plans'
import { useOrgBillingPlan } from '../Org/useBillingPlan'
import { SeatsPanel, useSeats } from '../Seats'
import { useInviteData } from '../useInviteData'

import { CreateInviteButton } from './CreateInviteButton'
import { showCreateInviteModal } from './CreateInviteModal'
import { MemberTable } from './MemberTable'
import { useSeatCapacity } from './useSeatCapacity'

import styles from './MemberSettings.module.scss'

// a lapsed workspace has no collaboration, so only the owner keeps a seat
const LAPSED_SEAT_MAX = 1

const LapsedPlanBanner = observer(function LapsedPlanBanner({
  org,
}: {
  org: IOrg
}) {
  const { user } = useMst()
  const { upgrade } = useUpgrade()
  const excess = org.members.length - LAPSED_SEAT_MAX

  if (!user.isCurrentOrgOwner) {
    return (
      <WarningAlert color="orange" title="This workspace’s plan has changed">
        This workspace’s plan has exceeded its maximum number of allotted seats.
        Current members will be restricted to read-only access, and no new
        members can be added.
      </WarningAlert>
    )
  }

  if (!org.hasRestrictedMembers) return null

  return (
    <WarningAlert color="orange" title="Action required">
      Your current plan allows for a maximum of {LAPSED_SEAT_MAX}{' '}
      {pluralize(LAPSED_SEAT_MAX, 'member')}. Remove {excess}{' '}
      {pluralize(excess, 'member')} to stay within your plan’s limit, or{' '}
      <Anchor
        component="button"
        c="orange.8"
        fz="inherit"
        underline="always"
        onClick={() => upgrade(org)}
      >
        upgrade your plan
      </Anchor>
      .
    </WarningAlert>
  )
})

const ScheduledPlanBanner = observer(function ScheduledPlanBanner({
  org,
  scheduled,
  toRemove,
}: {
  org: IOrg
  scheduled: ScheduledPlan
  toRemove: number
}) {
  const { user } = useMst()
  const { upgrade } = useUpgrade()
  const members = `${toRemove} ${pluralize(toRemove, 'member')}`

  return (
    <WarningAlert
      color="orange"
      title={`Your plan changes to ${scheduledPlanName(scheduled)} on ${formatPlanDate(scheduled.startsAt)}`}
    >
      It includes {scheduled.seatMax}{' '}
      {pluralize(scheduled.seatMax ?? 0, 'seat')}.{' '}
      {user.isCurrentOrgOwner ? (
        <>
          Remove {members} before then, or{' '}
          <Anchor
            component="button"
            c="orange.8"
            fz="inherit"
            underline="always"
            onClick={() => upgrade(org)}
          >
            change your plan
          </Anchor>
          .
        </>
      ) : (
        <>The workspace owner needs to remove {members} before then.</>
      )}
    </WarningAlert>
  )
})

const MemberSettingsInternal = observer(function MemberSettings({
  org,
}: {
  org: IOrg
}) {
  const { user } = useMst()

  const invites = useInviteData(org)
  const inviteCount = invites.openInviteCount
  const { workspace: seatData, pending: seatsPending } = useSeats(org)
  const seats = useSeatCapacity(org, {
    enabled: user.isCurrentOrgAdmin,
  })
  const capacity = seats.data
  const memberCount = org.members.length
  const billingPlan = useOrgBillingPlan(org).data
  const toRemove = membersToRemove(billingPlan)
  // Seats are shared across the account's workspaces, so inviting someone who
  // already holds a seat elsewhere costs nothing. Block invites when this
  // workspace is full; otherwise the API rejects invites that need an unavailable seat.
  const atSeatMax =
    !!capacity?.seatMax && memberCount + inviteCount >= capacity.seatMax
  const scheduledBanner =
    user.isCurrentOrgAdmin && billingPlan?.scheduledPlan && toRemove > 0 ? (
      <>
        <ScheduledPlanBanner
          org={org}
          scheduled={billingPlan.scheduledPlan}
          toRemove={toRemove}
        />
        <Space h="sm" />
      </>
    ) : null

  let countMeta = ''
  if (user.isCurrentOrgAdmin && inviteCount > 0) {
    countMeta += `${inviteCount} Open Invitation${
      inviteCount === 1 ? '' : 's'
    }, `
  }
  countMeta += `${memberCount} Workspace Member${memberCount === 1 ? '' : 's'}`

  const subheader = user.isCurrentOrgAdmin ? (
    <Group justify="space-between" align="center">
      <Title order={3}>{countMeta}</Title>
      <CreateInviteButton
        inviteCount={inviteCount}
        workspace={org}
        atSeatMax={atSeatMax}
        seatsPending={seats.pending}
      />
    </Group>
  ) : (
    <Title order={3}>{countMeta}</Title>
  )

  if (invites.pending || seatsPending) {
    return (
      <div className={styles.memberSettings_page}>
        <h1>Members</h1>
        <Loader size="sm" />
      </div>
    )
  }

  if (seatData) {
    return (
      <div className={styles.memberSettings_page}>
        <SeatsPanel
          org={org}
          header={
            <>
              <h1>Members</h1>
              {scheduledBanner}
              {subheader}
            </>
          }
          table={(emptySeats) => (
            <MemberTable
              workspace={org}
              emptySeats={{
                ...emptySeats,
                onInvite: user.isCurrentOrgAdmin
                  ? () => showCreateInviteModal({ org })
                  : undefined,
              }}
            />
          )}
        />
      </div>
    )
  }

  return (
    <div className={styles.memberSettings_page}>
      <h1>Members</h1>
      {org.isLapsedStripeAccount ? (
        <>
          <LapsedPlanBanner org={org} />
          <Space h="sm" />
        </>
      ) : (
        scheduledBanner
      )}
      {!org.isLapsedStripeAccount && user.isCurrentOrgAdmin && atSeatMax ? (
        <>
          <Toast
            type="warning"
            dismissable={false}
            message={
              <div>
                <Text fw="bold" fz={14}>
                  No seats available.
                </Text>
                <Text fz={14}>
                  This plan has a maximum of {capacity?.seatMax} seats. Remove a
                  member, revoke an invitation, or{' '}
                  {user.isCurrentOrgOwner
                    ? 'change your plan'
                    : 'ask the workspace owner to upgrade the plan'}{' '}
                  to invite more collaborators.
                </Text>
              </div>
            }
          />
          <Space h="sm" />
        </>
      ) : null}
      {subheader}
      <Space h="sm" />
      <MemberTable workspace={org} />
    </div>
  )
})

export const MemberSettings = () => {
  const { currentOrg } = useMst()
  if (!currentOrg) {
    return null
  }
  return <MemberSettingsInternal org={currentOrg} />
}
